import { useEffect } from "react";

import { Button, Typography } from "@bigbinary/neeto-atoms";
import DOMPurify from "dompurify";
import { useErrorDisplayStore } from "neetocommons/react-utils/useDisplayErrorPage";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet";
import { Trans, useTranslation } from "react-i18next";

import { ERRORS } from "./constants";

const sanitize = DOMPurify.sanitize;

const ErrorPage = ({
  homeUrl = "/",
  status,
  backToHomeButtonProps,
  children,
  showNeetoChatWidget = true,
  description,
  title,
  hideBackToHomeButton = false,
}) => {
  const { t } = useTranslation();
  const { statusCode: storeStatusCode, showErrorPage } =
    useErrorDisplayStore.pick();

  status = status || storeStatusCode;
  const { Image, errorKey, titleKey } = ERRORS[status] || ERRORS[404];

  useEffect(() => {
    const pageLoader = document.getElementById("neeto-page-loader");
    if (pageLoader) pageLoader.remove();

    const onBackButtonPress = () => {
      if (!showErrorPage) return;
      useErrorDisplayStore.setState({ showErrorPage: false });
    };

    window.addEventListener("popstate", onBackButtonPress);

    return () => {
      window.removeEventListener("popstate", onBackButtonPress);
    };
  }, []);

  const openChatWidget = () => {
    window.NeetoChat?.contextualHelp?.maximizeWidget();
    window.NeetoChat?.contextualHelp?.openWidget();
  };

  return (
    <div
      className="@container/error-page flex min-h-dvh w-full flex-col items-center justify-center"
      data-testid="error-page-container"
    >
      <div className="flex w-[768px] max-w-full flex-col gap-8 p-4 @[768px]/error-page:flex-row @[768px]/error-page:gap-12 @[768px]/error-page:p-6">
        <Image
          className="dark:[&_path]:fill-foreground w-full max-w-[180px] shrink-0 @[768px]/error-page:max-w-[240px]"
          data-testid="error-image"
        />
        <div className="flex flex-col justify-center md:min-w-0 md:flex-grow">
          <Helmet>
            <title>{t(titleKey)}</title>
          </Helmet>
          <Typography
            className="mb-4"
            data-testid="title"
            variant="h2"
            weight="semibold"
          >
            {title || <Trans i18nKey={errorKey} />}
          </Typography>
          {!!description && (
            <Typography
              className="mb-4"
              dangerouslySetInnerHTML={{ __html: sanitize(description) }}
              data-testid="description"
              variant="body1"
            />
          )}
          {showNeetoChatWidget && (
            <Typography className="mb-6" data-testid="title" variant="body1">
              <Trans
                i18nKey="neetoMolecules.errorPage.contactUs"
                components={{
                  button: (
                    <Button
                      className="h-auto p-0 text-[inherit]"
                      variant="link"
                      onClick={openChatWidget}
                    />
                  ),
                }}
              />
            </Typography>
          )}
          <div className="flex flex-col items-start gap-3 [&_a]:no-underline">
            {!hideBackToHomeButton && (
              <Button
                asChild
                size="lg"
                variant="default"
                {...backToHomeButtonProps}
              >
                <a href={homeUrl}>{t("neetoMolecules.errorPage.backToHome")}</a>
              </Button>
            )}
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

ErrorPage.propTypes = {
  /**
   * The URL to which the user will be taken to when user clicks on back to home button.
   */
  homeUrl: PropTypes.string,
  /**
   * The status code of the error.
   */
  status: PropTypes.oneOf([403, 404, 500]),
  /**
   * Decides whether 'contact us' link should be displayed or not.
   */
  showNeetoChatWidget: PropTypes.bool,
  /**
   * Description about the error.
   */
  description: PropTypes.string,
  /**
   * Decides whether 'back to home' button should be displayed or not.
   */
  hideBackToHomeButton: PropTypes.bool,
  /**
   * The title of the error page.
   */
  title: PropTypes.string,
};

export default ErrorPage;
